'use client';

import React from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '@/components/ui/accordion';
import {
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  Code,
  Brain,
  Shield,
  TrendingUp,
  FileText,
  ChevronRight,
  Timer,
  Award,
  Activity,
  Zap,
  Target,
  BarChart3,
  ShieldCheck,
  ShieldAlert,
  ClipboardCheck,
  Hash,
  ExternalLink
} from 'lucide-react';

interface ChallengeDetail {
  title: string;
  date: string;
  language: string;
  score: number;
  time_taken: number;
  session_recordings?: string[];
  meta?: {
    difficulty?: string;
    tags?: string[];
  };
}

interface McDetail {
  id: string;
  question: string;
  answer: string;
  correct: boolean;
  meta?: {
    tags?: string[];
  };
}

interface CheatingDetails {
  tab_leaving?: number;
  plagiarism?: string;
  pasted_code?: string;
  suspicious_activity?: boolean;
  ai_usage?: boolean;
}

interface AssessmentResult {
  total_challenges: number;
  mc_score: number;
  code_score: number;
  final_score: number;
  qualified: boolean;
  qualifying_score: number;
  cheating_flag?: string;
  cheating_details?: CheatingDetails;
  challenge_details?: ChallengeDetail[];
  mc_details?: McDetail[];
  scorecard?: Record<string, number>;
  time_taken?: number;
  admin_notes?: any;
}

interface AssessmentData {
  id: string;
  status: string;
  invited_at?: string;
  completed_at?: string;
  coding_assessment_results?: AssessmentResult[];
}

interface CodingAssessmentResultsProps {
  assessmentData: AssessmentData | null;
  loading: boolean;
  error: string | null;
}

const getScoreColor = (score: number, max: number = 100) => {
  const percentage = (score / max) * 100;
  if (percentage >= 80) return 'text-emerald-700';
  if (percentage >= 60) return 'text-amber-700';
  return 'text-rose-700';
};

const getScoreBgColor = (score: number, max: number = 100) => {
  const percentage = (score / max) * 100;
  if (percentage >= 80) return 'bg-emerald-50';
  if (percentage >= 60) return 'bg-amber-50';
  return 'bg-rose-50';
};

const getScoreBorderColor = (score: number, max: number = 100) => {
  const percentage = (score / max) * 100;
  if (percentage >= 80) return 'border-emerald-200';
  if (percentage >= 60) return 'border-amber-200';
  return 'border-rose-200';
};

const getProgressColor = (score: number, max: number = 100) => {
  const percentage = (score / max) * 100;
  if (percentage >= 80) return '[&>div]:bg-emerald-500';
  if (percentage >= 60) return '[&>div]:bg-amber-500';
  return '[&>div]:bg-rose-500';
};

export function CodingAssessmentResults({
  assessmentData,
  loading,
  error
}: CodingAssessmentResultsProps) {
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <Skeleton className="h-7 w-48 mb-2" />
                <Skeleton className="h-4 w-32" />
              </div>
              <Skeleton className="h-10 w-28 rounded-full" />
            </div>
            <div className="grid grid-cols-3 gap-6">
              <Skeleton className="h-24 rounded-lg" />
              <Skeleton className="h-24 rounded-lg" />
              <Skeleton className="h-24 rounded-lg" />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Skeleton className="h-40 rounded-xl" />
          <Skeleton className="h-40 rounded-xl" />
        </div>
      </div>
    );
  }

  if (error || !assessmentData) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8">
        <div className="flex flex-col items-center text-center">
          <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <AlertTriangle className="h-6 w-6 text-gray-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            No Assessment Data
          </h3>
          <p className="text-sm text-gray-600 max-w-sm">
            {error ||
              'No coding assessment data is available for this interview.'}
          </p>
        </div>
      </div>
    );
  }

  const results = assessmentData.coding_assessment_results?.[0];

  if (!results) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-6 border-b border-gray-200">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                Assessment In Progress
              </h3>
              <p className="text-sm text-gray-600">
                Invited on{' '}
                {new Date(assessmentData.invited_at || '').toLocaleDateString(
                  'en-US',
                  {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  }
                )}
              </p>
            </div>
            <div className="bg-amber-100 text-amber-700 px-3 py-1 rounded-full text-sm font-medium">
              Pending
            </div>
          </div>
        </div>
        <div className="p-6">
          <div className="flex items-center gap-3 text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-4">
            <Timer className="h-5 w-5 flex-shrink-0" />
            <p className="text-sm">
              The coding assessment has been sent to the candidate and is
              awaiting completion.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  return (
    <div className="space-y-6">
      {/* Hero Summary Card */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div
          className={`p-6 bg-gradient-to-br ${
            results.qualified
              ? 'from-emerald-50 via-green-50 to-teal-50'
              : 'from-rose-50 via-red-50 to-pink-50'
          }`}
        >
          <div className="flex items-start justify-between mb-6">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h2 className="text-xl font-semibold text-gray-900">
                  Assessment Results
                </h2>
                <Badge
                  className={`${
                    results.qualified
                      ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
                      : 'bg-rose-100 text-rose-800 border-rose-200'
                  } font-medium`}
                >
                  {results.qualified ? 'Qualified' : 'Not Qualified'}
                </Badge>
              </div>
              <p className="text-sm text-gray-600">
                Completed{' '}
                {assessmentData.completed_at
                  ? new Date(assessmentData.completed_at).toLocaleDateString(
                      'en-US',
                      {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      }
                    )
                  : 'Recently'}
              </p>
            </div>

            {/* Score Circle */}
            <div className="relative">
              <div
                className={`w-20 h-20 rounded-full ${getScoreBgColor(results.final_score)} border-2 ${getScoreBorderColor(results.final_score)} flex flex-col items-center justify-center`}
              >
                <span
                  className={`text-2xl font-bold ${getScoreColor(results.final_score)}`}
                >
                  {Math.round(results.final_score)}
                </span>
                <span className="text-xs text-gray-600">Score</span>
              </div>
            </div>
          </div>

          {/* Key Metrics */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white/70 backdrop-blur rounded-lg p-4 border border-white/50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-gray-600 uppercase tracking-wider">
                  Performance
                </span>
                <Target className="w-4 h-4 text-gray-400" />
              </div>
              <div className="flex items-baseline gap-1">
                <span
                  className={`text-2xl font-semibold ${getScoreColor(results.final_score)}`}
                >
                  {Math.round(results.final_score)}%
                </span>
                <span className="text-xs text-gray-500">
                  / {results.qualifying_score}% required
                </span>
              </div>
              <Progress
                value={results.final_score}
                className={`mt-2 h-1.5 ${getProgressColor(results.final_score)}`}
              />
            </div>

            <div className="bg-white/70 backdrop-blur rounded-lg p-4 border border-white/50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-gray-600 uppercase tracking-wider">
                  Duration
                </span>
                <Timer className="w-4 h-4 text-gray-400" />
              </div>
              <div className="flex items-baseline gap-1">
                <span className="text-2xl font-semibold text-gray-900">
                  {results.challenge_details?.[0]?.time_taken
                    ? formatDuration(results.challenge_details?.[0]?.time_taken)
                    : 'N/A'}
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-1">Assessment time</p>
            </div>

            {/* <div className="bg-white/70 backdrop-blur rounded-lg p-4 border border-white/50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-gray-600 uppercase tracking-wider">
                  Integrity
                </span>
                {results.cheating_flag === 'Not detected' ||
                !results.cheating_flag ? (
                  <ShieldCheck className="w-4 h-4 text-emerald-500" />
                ) : (
                  <ShieldAlert className="w-4 h-4 text-rose-500" />
                )}
              </div>
              <div className="flex items-baseline gap-1">
                <span
                  className={`text-lg font-semibold ${
                    results.cheating_flag === 'Not detected' ||
                    !results.cheating_flag
                      ? 'text-emerald-700'
                      : 'text-rose-700'
                  }`}
                >
                  {results.cheating_flag === 'Not detected' ||
                  !results.cheating_flag
                    ? 'Verified'
                    : 'Flagged'}
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {results.cheating_flag === 'Not detected' ||
                !results.cheating_flag
                  ? 'No issues detected'
                  : results.cheating_flag}
              </p>
            </div> */}
          </div>

          {/* Score Breakdown - moved into hero card */}
          {/* <div className="mt-6 pt-6 border-t border-white/30">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                Score Breakdown
              </h3>
              <BarChart3 className="w-4 h-4 text-gray-400" />
            </div>

            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Code className="w-4 h-4 text-gray-400" />
                    <span className="text-sm font-medium text-gray-700">
                      Coding Challenges
                    </span>
                  </div>
                  <span
                    className={`text-sm font-semibold ${getScoreColor(results.code_score)}`}
                  >
                    {Math.round(results.code_score)}%
                  </span>
                </div>
                <div className="bg-white/50 rounded-full h-2 overflow-hidden">
                  <div
                    className={`h-full transition-all duration-500 ease-out ${
                      results.code_score >= 80
                        ? 'bg-emerald-500'
                        : results.code_score >= 60
                          ? 'bg-amber-500'
                          : 'bg-rose-500'
                    }`}
                    style={{ width: `${results.code_score}%` }}
                  />
                </div>
              </div>
            </div>
          </div> */}
        </div>
      </div>

      {/* Score Breakdown - moved to hero card */}

      {/* Coding Challenges */}
      {results.challenge_details && results.challenge_details.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Code className="w-5 h-5 text-gray-600" />
                <h3 className="text-lg font-semibold text-gray-900">
                  Coding Challenges
                </h3>
                <Badge variant="secondary" className="ml-2">
                  {results.total_challenges} completed
                </Badge>
              </div>
            </div>
          </div>
          <div className="divide-y divide-gray-100">
            {results.challenge_details.map((challenge, index) => (
              <div
                key={index}
                className="p-6 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="font-semibold text-gray-900">
                        {challenge.title}
                      </h4>
                      {challenge.meta?.difficulty && challenge.meta.difficulty !== 'Ungraded' && (
                        <Badge
                          variant="outline"
                          className={`text-xs ${
                            challenge.meta.difficulty === 'Easy'
                              ? 'border-green-300 text-green-700'
                              : challenge.meta.difficulty === 'Medium'
                                ? 'border-amber-300 text-amber-700'
                                : 'border-red-300 text-red-700'
                          }`}
                        >
                          {challenge.meta.difficulty}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <span className="flex items-center gap-1.5">
                        <Hash className="w-3.5 h-3.5" />
                        {challenge.language}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <Timer className="w-3.5 h-3.5" />
                        {challenge.time_taken} min
                      </span>
                      {challenge.session_recordings && challenge.session_recordings.length > 0 && (
                        <a
                          href={challenge.session_recordings[0]}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1.5 text-blue-600 hover:text-blue-700 font-medium transition-colors"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                          View Recording
                        </a>
                      )}
                    </div>
                    {challenge.meta?.tags && challenge.meta.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {challenge.meta.tags.map((tag, tagIndex) => (
                          <span
                            key={tagIndex}
                            className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs font-medium"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="ml-6 text-right">
                    <div
                      className={`text-3xl font-bold ${getScoreColor(challenge.score, 10)}`}
                    >
                      {challenge.score}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">out of 10</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Multiple Choice Questions */}
      {results.mc_details && results.mc_details.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="mc-questions" className="border-0">
              <AccordionTrigger className="px-6 py-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-center justify-between w-full pr-4">
                  <div className="flex items-center gap-3">
                    <Brain className="w-5 h-5 text-gray-600" />
                    <span className="text-lg font-semibold text-gray-900">
                      Multiple Choice Questions
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                        <span className="text-sm font-medium text-emerald-700">
                          {results.mc_details.filter(q => q.correct).length}
                        </span>
                      </div>
                      <span className="text-gray-400">/</span>
                      <span className="text-sm text-gray-600">
                        {results.mc_details.length}
                      </span>
                    </div>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-6">
                <div className="space-y-3 pt-2">
                  {results.mc_details.map((question, index) => (
                    <div
                      key={question.id || index}
                      className={`border rounded-lg p-4 ${
                        question.correct
                          ? 'bg-emerald-50/50 border-emerald-200'
                          : 'bg-rose-50/50 border-rose-200'
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <div className="mt-0.5">
                          {question.correct ? (
                            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                          ) : (
                            <XCircle className="w-5 h-5 text-rose-600" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div
                            className="text-sm font-medium text-gray-900 mb-2"
                            dangerouslySetInnerHTML={{
                              __html: question.question
                            }}
                          />
                          <div className="flex items-center gap-2 text-sm">
                            <span className="text-gray-600">Answer:</span>
                            <code className="px-2 py-0.5 bg-white rounded border border-gray-200 font-mono text-xs">
                              {question.answer}
                            </code>
                          </div>
                          {question.meta?.tags &&
                            question.meta.tags.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {question.meta.tags.map((tag, tagIndex) => (
                                  <span
                                    key={tagIndex}
                                    className="px-2 py-0.5 bg-white/80 text-gray-600 rounded text-xs"
                                  >
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      )}

      {/* Integrity Check Details */}
      {/* {results.cheating_details && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Shield className="w-5 h-5 text-gray-600" />
              <h3 className="text-lg font-semibold text-gray-900">
                Integrity Report
              </h3>
            </div>
            {results.cheating_flag === 'Not detected' ||
            !results.cheating_flag ? (
              <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">
                <ShieldCheck className="w-3 h-3 mr-1" />
                Verified
              </Badge>
            ) : (
              <Badge className="bg-rose-100 text-rose-800 border-rose-200">
                <ShieldAlert className="w-3 h-3 mr-1" />
                Issues Detected
              </Badge>
            )}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div
              className={`rounded-lg border p-4 ${
                results.cheating_details.tab_leaving === 0
                  ? 'bg-emerald-50/50 border-emerald-200'
                  : 'bg-rose-50/50 border-rose-200'
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium text-gray-600 uppercase tracking-wider">
                  Tab Switches
                </span>
                {results.cheating_details.tab_leaving === 0 ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                ) : (
                  <AlertTriangle className="w-4 h-4 text-rose-500" />
                )}
              </div>
              <p
                className={`text-2xl font-bold ${
                  results.cheating_details.tab_leaving === 0
                    ? 'text-emerald-700'
                    : 'text-rose-700'
                }`}
              >
                {results.cheating_details.tab_leaving || 0}
              </p>
            </div>

            <div
              className={`rounded-lg border p-4 ${
                results.cheating_details.plagiarism === 'Not detected'
                  ? 'bg-emerald-50/50 border-emerald-200'
                  : 'bg-rose-50/50 border-rose-200'
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium text-gray-600 uppercase tracking-wider">
                  Plagiarism
                </span>
                {results.cheating_details.plagiarism === 'Not detected' ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                ) : (
                  <AlertTriangle className="w-4 h-4 text-rose-500" />
                )}
              </div>
              <p
                className={`text-lg font-bold ${
                  results.cheating_details.plagiarism === 'Not detected'
                    ? 'text-emerald-700'
                    : 'text-rose-700'
                }`}
              >
                {results.cheating_details.plagiarism === 'Not detected'
                  ? 'None'
                  : 'Detected'}
              </p>
            </div>

            <div
              className={`rounded-lg border p-4 ${
                results.cheating_details.pasted_code === 'Not detected'
                  ? 'bg-emerald-50/50 border-emerald-200'
                  : 'bg-rose-50/50 border-rose-200'
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium text-gray-600 uppercase tracking-wider">
                  Code Pasting
                </span>
                {results.cheating_details.pasted_code === 'Not detected' ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                ) : (
                  <AlertTriangle className="w-4 h-4 text-rose-500" />
                )}
              </div>
              <p
                className={`text-lg font-bold ${
                  results.cheating_details.pasted_code === 'Not detected'
                    ? 'text-emerald-700'
                    : 'text-rose-700'
                }`}
              >
                {results.cheating_details.pasted_code === 'Not detected'
                  ? 'None'
                  : 'Detected'}
              </p>
            </div>

            <div
              className={`rounded-lg border p-4 ${
                !results.cheating_details.ai_usage
                  ? 'bg-emerald-50/50 border-emerald-200'
                  : 'bg-rose-50/50 border-rose-200'
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium text-gray-600 uppercase tracking-wider">
                  AI Usage
                </span>
                {!results.cheating_details.ai_usage ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                ) : (
                  <AlertTriangle className="w-4 h-4 text-rose-500" />
                )}
              </div>
              <p
                className={`text-lg font-bold ${
                  !results.cheating_details.ai_usage
                    ? 'text-emerald-700'
                    : 'text-rose-700'
                }`}
              >
                {!results.cheating_details.ai_usage ? 'None' : 'Detected'}
              </p>
            </div>

            <div
              className={`rounded-lg border p-4 ${
                !results.cheating_details.suspicious_activity
                  ? 'bg-emerald-50/50 border-emerald-200'
                  : 'bg-rose-50/50 border-rose-200'
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium text-gray-600 uppercase tracking-wider">
                  Suspicious
                </span>
                {!results.cheating_details.suspicious_activity ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                ) : (
                  <AlertTriangle className="w-4 h-4 text-rose-500" />
                )}
              </div>
              <p
                className={`text-lg font-bold ${
                  !results.cheating_details.suspicious_activity
                    ? 'text-emerald-700'
                    : 'text-rose-700'
                }`}
              >
                {!results.cheating_details.suspicious_activity ? 'None' : 'Yes'}
              </p>
            </div>
          </div>
        </div>
      )} */}

      {/* Admin Notes */}
      {results.admin_notes?.ai_project_analysis && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="admin-notes" className="border-0">
              <AccordionTrigger className="px-6 py-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-gray-600" />
                  <span className="text-lg font-semibold text-gray-900">
                    AI Analysis & Admin Notes
                  </span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-6">
                <div className="space-y-3 pt-2">
                  {Object.entries(results.admin_notes.ai_project_analysis).map(
                    ([key, value], index) => (
                      <div
                        key={key}
                        className="bg-gray-50 rounded-lg p-4 border border-gray-100"
                      >
                        <p className="text-sm text-gray-700 leading-relaxed">
                          {value as string}
                        </p>
                      </div>
                    )
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      )}
    </div>
  );
}
